import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import Button from "@/v2-backoffice-client/components/button"
import LicensePlanSelect from "@/v2-backoffice-client/license/LicensePlanSelect"
import { useLicenseUpdateMutation } from "@/v2-backoffice-client/license/update/license.update.mutation"
import { evictLicenseQueries } from "@/v2-backoffice-client/license/license.utils"
import { LICENSE_PLAN_NONE_VALUE } from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseChangePlanMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  license: LicenseFragment
}

// Changes which catalog license an operator grant is a license of, in place
// (FUS-2766). Until this existed a grant that landed on the wrong license
// (an unmapped Fenerum plan under an MCP product, "None") could only be
// ended and granted again. The picker applies the same rules as the grant:
// the product's own licenses only, and for a seated product one is
// required. Billing-derived rows never get here: the sync owns their plan.
function LicenseChangePlanMenuItem(props: LicenseChangePlanMenuItemProps) {
  const { license, children, ...menuItemProps } = props
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <ActionMenu.Item
        iconSrc="tags"
        data-feature-code="UI_BackofficeLicense_Row_ChangePlan"
        onSelect={(e) => {
          e.preventDefault()
          setIsOpen(true)
        }}
        {...menuItemProps}
      >
        {children ?? "Update License"}
      </ActionMenu.Item>

      {/* Mounted per open so the picker re-seeds from the row every time. */}
      {isOpen && (
        <LicenseChangePlanDialog
          license={license}
          open={isOpen}
          onOpenChange={setIsOpen}
        />
      )}
    </>
  )
}

type LicenseChangePlanDialogProps = {
  license: LicenseFragment
  open: boolean
  onOpenChange(open: boolean): void
}

function LicenseChangePlanDialog(props: LicenseChangePlanDialogProps) {
  const { license, open, onOpenChange } = props
  const [planId, setPlanId] = useState(
    license.PlanId != null ? String(license.PlanId) : LICENSE_PLAN_NONE_VALUE,
  )

  const [updateLicense, { loading, error }] = useLicenseUpdateMutation({
    update: evictLicenseQueries,
    onCompleted(data) {
      const updated = data.updateLicense
      toast({
        variant: "success",
        title: "License changed",
        description: updated.PlanName
          ? `${updated.CustomerName ?? "The customer"} is now on ${updated.PlanName}; its packages apply from the next read.`
          : `${updated.CustomerName ?? "The customer"}'s ${updated.ProductName} grant is on no license now: nothing from packages, add-ons only.`,
      })
      onOpenChange(false)
    },
  })

  const isUnchanged =
    planId ===
    (license.PlanId != null ? String(license.PlanId) : LICENSE_PLAN_NONE_VALUE)
  const canSave =
    !isUnchanged &&
    (!license.SeatsAreAssigned || planId !== LICENSE_PLAN_NONE_VALUE)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Update License — {license.ProductName}</Dialog.Title>
          <Dialog.Description>
            Which license {license.CustomerName ?? "the customer"} is on for
            this product. Its packages decide what their users get; the change
            applies from the next read, nothing else about the grant moves.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-2">
          <Label>License</Label>
          <LicensePlanSelect
            value={planId}
            productCode={String(license.ProductCode)}
            requirePlan={license.SeatsAreAssigned}
            onValueChange={setPlanId}
          />
          {license.SeatsAreAssigned && (
            <p className="text-xs text-muted-foreground">
              A license of this product needs one from the catalog: its packages
              are what let people in.
            </p>
          )}
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            disabled={!canSave}
            data-feature-code="UI_BackofficeLicense_ChangePlan_Save"
            onClick={() => {
              updateLicense({
                variables: {
                  id: license.Id,
                  input:
                    planId === LICENSE_PLAN_NONE_VALUE
                      ? { ClearPlanId: true }
                      : { PlanId: Number(planId) },
                },
              })
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseChangePlanMenuItem
